import { Fragment, useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'
import { convertTZ, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { offerHasValidDepartures } from 'lib/cruises/cruiseUtils'
import { hasCruiseBundleRates } from 'lib/cruises/cruiseBookingFlow'
import { getCruiseDepartureCabinPrices, isCruiseDepartureSoldOut } from 'lib/cruises/cruiseDeparturePricing'
import useCruiseDeparturesModel from 'hooks/Cruise/useCruiseDeparturesModel'
import { useCruiseMemberPricing } from 'hooks/Cruise/useCruiseMemberPricing'
import CruiseTravellerSelectionPicker from 'components/Cruises/Common/Departures/CruiseTravellerSelectionPicker'
import CruiseDepartureSortDropdown from 'components/Cruises/Common/Departures/CruiseDepartureSortDropdown'
import CruiseDepartureCabinPriceItem from 'components/Cruises/Common/Departures/CruiseDepartureCabinPriceItem'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import CruiseSoldoutDepartureCard from 'components/OfferPage/Cruises/CruiseSoldoutDepartureCard'
import CruiseSoldoutStillInterested from 'components/OfferPage/Cruises/CruiseSoldoutStillInterested'
import MonthTabButtonsGroup from 'components/OfferPage/TourV2/Departures/MonthTabs/MonthTabButtonsGroup'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import FilterChip from 'LuxKit/Chips/FilterChip'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Divider from 'LuxKit/Divider'
import useDrawerDividerKind from '../../hooks/useDrawerDividerKind'
import Heading from 'LuxKit/Typography/Heading'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import type { DeparturesByDate } from 'lib/cruises/cruiseDepartures'

const Controls = styled.div`
  display: grid;
  gap: ${rem(12)};
  min-width: 0;

  ${mediaQueryUp.tablet} {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }
`

const ControlsPrimary = styled(Group)`
  min-width: 0;
`

const GuestControl = styled.div`
  flex: 0 0 auto;
`

const SortControl = styled.div`
  flex: 0 0 auto;
`

const YearTabsScrollWrap = styled.div`
  flex: 0 1 auto;
  width: min(100%, ${rem(320)});
  min-width: 0;
  max-width: 100%;
`

const DEPARTURE_ROW_MIN_WIDTH = rem(1040)

const DepartureCard = styled.div`
  min-width: 0;
  container-type: inline-size;
`

const DepartureLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'summary actions'
    'prices prices';
  gap: ${rem(16)};
  min-width: 0;

  @container (min-width: ${DEPARTURE_ROW_MIN_WIDTH}) {
    grid-template-columns: minmax(${rem(220)}, max-content) minmax(0, 1fr) auto;
    grid-template-areas: 'summary prices actions';
    align-items: center;
    column-gap: ${rem(20)};
  }
`

const DepartureSummary = styled(Group)`
  grid-area: summary;
  min-width: 0;
`

const PricesGrid = styled.div<{ $columnCount: number }>`
  grid-area: prices;
  display: grid;
  column-gap: ${rem(12)};
  row-gap: ${rem(20)};
  grid-template-columns: repeat(${props => props.$columnCount}, minmax(${rem(128)}, 1fr));
  overflow-x: auto;
`

const PriceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: 0;

  & + & {
    border-inline-start: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
    padding-inline-start: ${rem(12)};
  }
`

const Actions = styled(Group)`
  grid-area: actions;
  flex-direction: row;
  justify-self: end;
  flex-shrink: 0;

  @container (min-width: ${DEPARTURE_ROW_MIN_WIDTH}) {
    && {
      flex-direction: column;
    }
  }
`

interface YearFilterChipProps {
  count: number
  isSelected: boolean
  onSelect: (year: number) => void
  year: number
}

function YearFilterChip({ count, isSelected, onSelect, year }: YearFilterChipProps) {
  const intl = useIntl()
  const handleSelect = useCallback(() => {
    onSelect(year)
  }, [onSelect, year])

  return <FilterChip selected={isSelected} size="medium" onClick={handleSelect}>
    {intl.formatMessage({
      id: 'kOl3zm6IRLrJe0xbbkWq',
      defaultMessage: '{year} ({count})',
      description: 'Filter chip for a departure year with count of departures in the cruises section',
    }, { year, count })}
  </FilterChip>
}

interface DepartureTileProps {
  offer: App.CruiseOffer
  departure: App.CruiseDepartureDetails
  hasItinerarySelection: boolean
  itineraryDepartureId?: string
  itineraryItemId?: number
  isItineraryActionDisabled: boolean
  isCheckoutDisabled: boolean
  onDepartureItineraryAction: (departure: App.CruiseDepartureDetails) => void
  onCheckoutDeparture: (departure: App.CruiseDepartureDetails) => void
}

function CruiseDrawerDepartureTile({
  offer,
  departure,
  hasItinerarySelection,
  itineraryDepartureId,
  itineraryItemId,
  isItineraryActionDisabled,
  isCheckoutDisabled,
  onDepartureItineraryAction,
  onCheckoutDeparture,
}: DepartureTileProps) {
  const dateFormatter = useIntlDateFormatter()
  const { departureMemberPricingByCategory } = useCruiseMemberPricing(offer, departure)
  const isSoldOut = isCruiseDepartureSoldOut(offer.lowestDeparturePricesByCategory, departure.id)
  const prices = useMemo(
    () => getCruiseDepartureCabinPrices(offer, departure, departureMemberPricingByCategory),
    [departure, departureMemberPricingByCategory, offer],
  )
  const dates = useMemo(() => {
    const departureDate = convertTZ(new Date(departure.departureDate!), 'UTC')
    const arrivalDate = convertTZ(new Date(departure.arrivalDate!), 'UTC')

    return {
      arrivalLabel: dateFormatter(arrivalDate, INTL_DMY_CASUAL_FORMAT),
      arrivalWeekdayLabel: dateFormatter(arrivalDate, INTL_LONG_WEEKDAY),
      departureLabel: dateFormatter(departureDate, INTL_DMY_CASUAL_FORMAT),
      departureWeekdayLabel: dateFormatter(departureDate, INTL_LONG_WEEKDAY),
    }
  }, [dateFormatter, departure.arrivalDate, departure.departureDate])

  const handleItineraryAction = useCallback(() => {
    onDepartureItineraryAction(departure)
  }, [departure, onDepartureItineraryAction])
  const handleCheckout = useCallback(() => {
    onCheckoutDeparture(departure)
  }, [departure, onCheckoutDeparture])
  const isSavedDeparture = hasItinerarySelection &&
    itineraryDepartureId === departure.id &&
    itineraryItemId !== undefined

  return <DepartureCard data-testid="cruise-drawer-departure-card">
    <DepartureLayout>
      <DepartureSummary direction="vertical" gap={12}>
        <Group direction="horizontal" gap={12}>
          <div>
            <Heading variant="heading4" wrap="no-wrap">{dates.departureLabel}</Heading>
            <BodyText variant="small" weight="normal" colour="light-foreground-alt">
              {dates.departureWeekdayLabel}
            </BodyText>
          </div>
          <LineArrowRightIcon />
          <div>
            <Heading variant="heading4" wrap="no-wrap">{dates.arrivalLabel}</Heading>
            <BodyText variant="small" weight="normal" colour="light-foreground-alt">
              {dates.arrivalWeekdayLabel}
            </BodyText>
          </div>
        </Group>

        <CruiseLoyaltyPoints offer={offer} calculationType="estimate" />
      </DepartureSummary>

      <Actions direction="horizontal" gap={8}>
        {!isSoldOut && <TextButton kind="primary" disabled={isCheckoutDisabled} onClick={handleCheckout}>
          <FormattedMessage
            id="Jq2GNkHEV-kr4jZFhc1Y"
            defaultMessage="Book now"
            description="CTA button text to book a cruise departure in trip planner"
          />
        </TextButton>}
        <TextButton
          kind="secondary"
          disabled={isItineraryActionDisabled}
          onClick={handleItineraryAction}
        >
          {isSavedDeparture ?
              <FormattedMessage {...tripPlannerGlossaryMessages.removeFromTrip} /> :
              <FormattedMessage {...tripPlannerGlossaryMessages.addToTrip} />}
        </TextButton>
      </Actions>

      <PricesGrid $columnCount={Math.max(prices.length, 1)}>
        {prices.map((price) => <PriceItem key={price.category}>
          <CruiseDepartureCabinPriceItem
            offer={offer}
            price={price}
          />
        </PriceItem>)}

        {prices.length === 0 && <BodyTextBlock variant="medium" colour="light-foreground-alt">
          <FormattedMessage
            id="MS-KJ65nJRQALFaMLLgk"
            defaultMessage="No prices available"
            description="Empty state message shown when no cabin prices are available for a cruise departure"
          />
        </BodyTextBlock>}
      </PricesGrid>
    </DepartureLayout>
  </DepartureCard>
}

interface Props {
  offer: App.CruiseOffer
  occupancies: Array<App.Occupants>
  setOccupancies: (occupancies: Array<App.Occupants>) => void
  hasItinerarySelection: boolean
  itineraryDepartureId?: string
  itineraryItemId?: number
  isItineraryActionDisabled: boolean
  isCheckoutDisabled: boolean
  onDepartureItineraryAction: (departure: App.CruiseDepartureDetails) => void
  onCheckoutDeparture: (departure: App.CruiseDepartureDetails) => void
}

function CruiseDrawerDepartures({
  offer,
  occupancies,
  setOccupancies,
  hasItinerarySelection,
  itineraryDepartureId,
  itineraryItemId,
  isItineraryActionDisabled,
  isCheckoutDisabled,
  onDepartureItineraryAction,
  onCheckoutDeparture,
}: Props) {
  const intl = useIntl()
  const dividerKind = useDrawerDividerKind()
  const departuresByDate = (offer.departures ?? {}) as DeparturesByDate
  const hasValidDepartures = offerHasValidDepartures(offer)
  const canSelectTravellers = !hasCruiseBundleRates(offer)
  const {
    departureCountByYear,
    departuresByMonth,
    hasMultipleDepartures,
    referenceDepartureDateForOccupancy,
    selectedYear,
    setSelectedYear,
    setSort,
    sort,
    totalDepartureCount,
    yearOptions,
  } = useCruiseDeparturesModel({
    departuresByDate,
    lowestDeparturePricesByCategory: offer.lowestDeparturePricesByCategory,
  })
  const handleSelectAllYears = useCallback(() => {
    setSelectedYear(undefined)
  }, [setSelectedYear])

  return <Group direction="vertical" gap={24}>
    <Heading variant="heading3">
      <FormattedMessage
        id="A51U7aAfppFcGWSpIvSj"
        defaultMessage="Dates & Pricing"
        description="Section heading for cruise departure dates and pricing"
      />
    </Heading>

    <Controls>
      <ControlsPrimary direction="vertical" tabletDirection="horizontal" tabletVerticalAlign="center" gap={12}>
        {canSelectTravellers && <GuestControl>
          <CruiseTravellerSelectionPicker
            ship={offer.ship}
            occupancies={occupancies}
            onChange={setOccupancies}
            departureDate={referenceDepartureDateForOccupancy ?? ''}
          />
        </GuestControl>}

        {yearOptions.length > 1 && <YearTabsScrollWrap>
          <MonthTabButtonsGroup fitContent>
            <Group direction="horizontal" gap={8}>
              <FilterChip selected={!selectedYear} size="medium" onClick={handleSelectAllYears}>
                {intl.formatMessage({
                  id: '8g3Gj5D6eyt0V6yvmlN4',
                  defaultMessage: 'All ({count})',
                  description: 'Filter chip label to show all departure years in the cruises section, with total departure count',
                }, { count: totalDepartureCount })}
              </FilterChip>
              {yearOptions.map((year) => <YearFilterChip
                key={year}
                count={departureCountByYear[year] ?? 0}
                isSelected={selectedYear === year}
                onSelect={setSelectedYear}
                year={year}
              />)}
            </Group>
          </MonthTabButtonsGroup>
        </YearTabsScrollWrap>}
      </ControlsPrimary>

      {hasMultipleDepartures && <SortControl>
        <CruiseDepartureSortDropdown sort={sort} onChange={setSort} />
      </SortControl>}
    </Controls>

    {isCheckoutDisabled && <BodyTextBlock variant="medium" colour="light-foreground-alt">
      <FormattedMessage
        id="4ayT52HxOuM-2GjmeKzH"
        defaultMessage="You already have a cruise in checkout. Complete or remove it before selecting another cruise."
        description="Message explaining why a cruise departure cannot be selected while another cruise checkout is active"
      />
    </BodyTextBlock>}

    <Group direction="vertical" gap={28}>
      {departuresByMonth.map(([dateLabel, departures]) => <Group direction="vertical" gap={12} key={dateLabel}>
        <BodyText variant="large" weight="bold">{dateLabel}</BodyText>
        {departures.map(({ departure }) => <Fragment key={departure.id}>
          <CruiseDrawerDepartureTile
            offer={offer}
            departure={departure}
            hasItinerarySelection={hasItinerarySelection}
            itineraryDepartureId={itineraryDepartureId}
            itineraryItemId={itineraryItemId}
            isItineraryActionDisabled={isItineraryActionDisabled}
            isCheckoutDisabled={isCheckoutDisabled}
            onDepartureItineraryAction={onDepartureItineraryAction}
            onCheckoutDeparture={onCheckoutDeparture}
          />
          <Divider kind={dividerKind} />
        </Fragment>)}
      </Group>)}

      {!hasValidDepartures && <CruiseSoldoutDepartureCard />}
      {!hasValidDepartures && <CruiseSoldoutStillInterested useLinkButton />}
    </Group>
  </Group>
}

export default CruiseDrawerDepartures
